import {
  type AccountStatus,
  type PaymentIssue,
  type PremiumFeatures,
} from '@/services/types/premiumFeatures/premiumFeatures'
import { START_THREATEN_DAY } from '@/utils/constants/premiumFeatures'
import { differenceInDays, isAfter } from 'date-fns'

import { create } from 'zustand'

export interface PremiumFeaturesActions {
  setPremiumFeatures: (premiumFeatures: PremiumFeatures[]) => void
  setStatus: (status: AccountStatus) => void
  setPaymentIssue: (paymentIssue: PaymentIssue) => void
}

export interface PremiumFeaturesState {
  actions: PremiumFeaturesActions
  premiumFeatures: PremiumFeatures[]
  isLoading: boolean
  status: AccountStatus | null
  paymentIssue?: {
    paymentAttempts: number
    lastAttempt: Date
    gracePeriod: Date
    shouldThreat?: boolean
    shouldWarn?: boolean
    daysLeftToSuspend?: number
  }
}

const INITIAL_STATE: Omit<PremiumFeaturesState, 'actions'> = {
  premiumFeatures: [],
  isLoading: true,
  status: null,
}

export const usePremiumFeaturesStore = create<PremiumFeaturesState>()((set) => ({
  ...INITIAL_STATE,
  actions: {
    setPremiumFeatures: (premiumFeatures: PremiumFeatures[]) => {
      set(() => ({ premiumFeatures, isLoading: false }))
    },
    setStatus: (status: AccountStatus) => {
      set(() => ({ status }))
    },
    setPaymentIssue: (paymentIssue: PaymentIssue) => {
      set(() => {
        const isGracePeriodAfter = isAfter(paymentIssue.gracePeriod, new Date())

        const daysUntilGracePeriod = differenceInDays(paymentIssue.gracePeriod, new Date())

        return {
          paymentIssue: {
            paymentAttempts: paymentIssue.paymentAttempts,
            lastAttempt: paymentIssue.lastAttempt,
            gracePeriod: paymentIssue.gracePeriod,
            ...(isGracePeriodAfter && {
              shouldWarn: daysUntilGracePeriod > START_THREATEN_DAY,
              shouldThreat: daysUntilGracePeriod <= START_THREATEN_DAY,
              daysLeftToSuspend: daysUntilGracePeriod,
            }),
          },
        }
      })
    },
  },
}))

export const usePremiumFeaturesPermissions = () =>
  usePremiumFeaturesStore((state) => state.premiumFeatures)
export const usePremiumFeaturesStatus = () => usePremiumFeaturesStore((state) => state.status)
export const usePremiumFeaturesPaymentIssue = () =>
  usePremiumFeaturesStore((state) => state.paymentIssue)
